import { Link } from "react-router-dom";
import ProductAppsFolder from "../ProductAppsFolder";
import { useOpenSpace } from "../../context/SpaceGateContext";
import Avatar from "../ds/Avatar";
import Badge from "../ds/Badge";
import Tooltip from "../ds/Tooltip";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import ProgressBar from "../ds/ProgressBar";
import { PlatformLogo, platformForRateKey } from "../campaign/shared";
import UsernameLink from "../UsernameLink";
import CardMedia from "./CardMedia";
import { CARD_SURFACE } from "./DiscoverCampaignCard";
import { PaidOutOf, STATUS_KEYS, STATUS_TONES, campaignRateText, daysLeft, formatMoney, formatNumber, formatRating, ownerName } from "./campaignDisplay";
import { getParticipantTypeLabel } from "../../utils/campaignCardUtils";
import { getPerPlatformRateRows, getPlatformShortLabel } from "../../utils/campaignRates";
import { getProfileAvatarUrl } from "../../utils/userDisplay";

/*
  Discover's "All" view (ყველა), from Figma "Featured 2 · კარუსელი · 1440": a few
  of each kind under its own heading, each with "View all" to its tab. These
  tiles carry more than the per-tab cards: platform rates, approval and a join button for
  campaigns, the product type and an access button for products, rating and
  counts for spaces.
*/

export const Section = ({ title, onViewAll, viewAllLabel, children }) => (
  <section className="flex flex-col gap-space-12 lg:gap-[14px]" aria-label={title}>
    <div className="flex items-center justify-between gap-space-12">
      <h2 className="text-caps-section-title uppercase text-label-primary">{title}</h2>
      {onViewAll && (
        <Button variant="plain" size="m" onClick={onViewAll}>
          {viewAllLabel}
        </Button>
      )}
    </div>
    {children}
  </section>
);

export const openOnKey = (open) => (event) => {
  if (event.key === "Enter") open();
};

const COVER_BADGE =
  "inline-flex h-[24px] items-center whitespace-nowrap rounded-radius-sm bg-fill-secondary px-space-8 text-caps-button-s uppercase text-label-primary";

export const SpaceLink = ({ space, children, className = "min-w-0 truncate text-footnote text-label-secondary" }) => {
  const openSpace = useOpenSpace();
  return (
    <Link
      to={`/mint-space/${space._id}`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        openSpace(space);
      }}
      className={`${className} transition hover:text-accent-text`}
    >
      {children}
    </Link>
  );
};

export const CampaignTile = ({ campaign, onOpen, isSelected = false, showJoin = true, joinLabel, footer = null, language, t }) => {
  const media = campaign.thumbnail || campaign.campaignLogo || "";
  const isVideo = Boolean(media) && (campaign.thumbnailType || "image") === "video";
  const budget = Number(campaign.budget) || 0;
  const paid = Number(campaign.spent) || 0;
  const days = daysLeft(campaign);
  const creators = campaign.creatorCount || 0;
  const meta = [
    days === null ? null : days <= 1 ? t("home.daysLeftOne") : t("home.daysLeft", { count: days }),
    creators > 0 ? t(creators === 1 ? "home.creatorsOne" : "home.creatorsOther", { count: creators }) : null,
  ].filter(Boolean);
  const organizer = campaign.mintSpace?.name || ownerName(campaign.createdBy);
  const platforms = getPerPlatformRateRows(campaign).filter((row) => row.key);
  const stats = campaign.submissionStats;
  const open = () => onOpen(campaign);

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={openOnKey(open)}
      className={`${CARD_SURFACE} cursor-pointer ${isSelected ? "outline outline-1 -outline-offset-1 outline-accent-default" : ""}`}
    >
      <CardMedia
        src={media}
        isVideo={isVideo}
        placeholderIcon="megaphone"
        placeholderLabel={t("home.mediaCampaign")}
        className="h-[203px]"
      >
        <div className="absolute inset-x-space-12 top-space-12 flex flex-wrap gap-space-4">
          {campaign.participantType === "creators" ? (
            <Tooltip label={t("campaignFlow.ugcHint")}>
              <span className={COVER_BADGE}>{getParticipantTypeLabel(t, campaign.participantType)}</span>
            </Tooltip>
          ) : (
            <span className={COVER_BADGE}>{getParticipantTypeLabel(t, campaign.participantType)}</span>
          )}
          <span className={COVER_BADGE}>
            {t("home.approval")} {stats?.total > 0 ? `${Math.round(stats.approvalRate)}%` : "—"}
          </span>
        </div>
      </CardMedia>
      <div className="flex flex-1 flex-col gap-space-12 p-space-16">
        <p className="flex flex-wrap items-baseline gap-x-[6px] whitespace-nowrap">
          <span className="text-title-2 text-label-primary">{campaignRateText(campaign, language)}</span>
          <span className="text-subheadline text-label-secondary">{t("home.perThousandViews")}</span>
        </p>
        <h3 className="line-clamp-2 text-headline text-label-primary">{campaign.campaignName}</h3>
        {organizer && (
          // Its own link inside the card: Enter here must not open the campaign too.
          <div className="flex min-w-0 items-center gap-space-8" onKeyDown={(event) => event.stopPropagation()}>
            <Avatar src={campaign.mintSpace?.logo} name={organizer} />
            {campaign.mintSpace?._id ? (
              <SpaceLink space={campaign.mintSpace}>{organizer}</SpaceLink>
            ) : (
              <UsernameLink
                user={campaign.createdBy}
                username={organizer}
                prefix=""
                stopPropagation
                className="min-w-0 truncate text-footnote text-label-secondary"
              />
            )}
          </div>
        )}
        {platforms.length > 0 && (
          <div className="flex flex-wrap gap-[6px]">
            {platforms.map((row) => (
              <Badge key={row.key} size="s" className="gap-space-4">
                <PlatformLogo platform={platformForRateKey(row.key)} size={12} />
                <span className="sr-only">{getPlatformShortLabel(row.key)}</span>
                {formatMoney(row.rate, language)}
              </Badge>
            ))}
          </div>
        )}
        {budget > 0 && (
          <div className="flex flex-col gap-[6px]">
            <div className="flex flex-wrap items-start gap-x-space-4 whitespace-nowrap">
              <span className="flex-1 text-caps-label uppercase text-label-secondary">{t("home.budget")}</span>
              <PaidOutOf paid={paid} total={budget} language={language} t={t} />
            </div>
            <ProgressBar value={paid / budget} />
          </div>
        )}
        <div className="mt-auto flex min-w-0 flex-wrap items-center gap-x-space-8 gap-y-space-4">
          <Badge size="s" tone={STATUS_TONES[campaign.status] || "warning"}>
            {t(STATUS_KEYS[campaign.status] || STATUS_KEYS.pending)}
          </Badge>
          {meta.length > 0 && <span className="text-footnote text-label-secondary">{meta.join(" · ")}</span>}
        </div>
        {showJoin && (
          <Button
            size="m"
            fullWidth
            onClick={(event) => {
              event.stopPropagation();
              open();
            }}
          >
            {joinLabel || t("home.joinCampaign")}
          </Button>
        )}
        {footer}
      </div>
    </div>
  );
};

export const ReviewsRow = ({ average, count, language, t }) => (
  <p className="flex items-center gap-space-4 text-footnote">
    <span className="text-status-warning" aria-hidden="true">
      <Icon name="star" size={14} />
    </span>
    {count > 0 && <span className="text-footnote-semibold text-label-primary">{formatRating(average, language)}</span>}
    <span className="text-label-secondary">
      {t(count === 1 ? "home.reviewsCountOne" : "home.reviewsCount", { count: formatNumber(count, language) })}
    </span>
  </p>
);

export const PERIOD_KEYS = { "1w": "home.period1w", "1m": "home.period1m", "3m": "home.period3m", "6m": "home.period6m", "1y": "home.period1y" };

export const ProductTile = ({ space, product, categoryLabel, apps = null, onOpen, language, t }) => {
  const isFree = product.accessType === "free";
  const media = product.bannerImage || product.listingBanner || "";
  const mediaType = product.bannerImage ? product.bannerType || "image" : product.listingBannerType || "image";
  const billing = isFree
    ? ""
    : product.billingType === "recurring"
    ? `/ ${t(PERIOD_KEYS[product.period] || PERIOD_KEYS["1m"])}`
    : t("home.oneTime");
  const open = () => onOpen(`/mint-space/${space._id}/product/${product._id}`);

  return (
    <div role="link" tabIndex={0} onClick={open} onKeyDown={openOnKey(open)} className={`${CARD_SURFACE} cursor-pointer`}>
      <CardMedia
        src={media}
        isVideo={Boolean(media) && mediaType === "video"}
        placeholderIcon="bag"
        placeholderLabel={t("home.mediaProduct")}
        className="h-[240px]"
      >
        {apps?.length > 0 && (
          <ProductAppsFolder spaceId={space._id} title={product.name} apps={apps} className="absolute right-2 top-2" />
        )}
      </CardMedia>
      <div className="flex flex-1 flex-col gap-[6px] p-[14px]">
        <p className="flex items-baseline gap-space-4 whitespace-nowrap">
          <span className="text-title-3 text-label-primary">
            {isFree ? t("mintSpace.free") || "Free" : formatMoney(product.price ?? 0, language)}
          </span>
          {billing && <span className="text-footnote text-label-secondary">{billing}</span>}
        </p>
        <h3 className="line-clamp-2 text-subheadline-semibold text-label-primary">
          {product.name || t("mintSpace.untitledProduct") || "Untitled product"}
        </h3>
        {product.headline && <p className="line-clamp-2 text-footnote text-label-secondary">{product.headline}</p>}
        {categoryLabel && <p className="truncate text-caption-1 text-label-secondary">{categoryLabel}</p>}
        {/* Its own link inside the card: Enter here must not open the product too. */}
        <div className="flex min-w-0 items-center gap-space-8" onKeyDown={(event) => event.stopPropagation()}>
          <Avatar src={space.logo} name={space.name} />
          <SpaceLink space={space}>{space.name}</SpaceLink>
        </div>
        <ReviewsRow average={product.averageRating} count={product.ratingCount ?? 0} language={language} t={t} />
        <Button
          variant="tinted"
          size="s"
          fullWidth
          className="mt-auto"
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
        >
          {t("home.getAccess")}
        </Button>
      </div>
    </div>
  );
};

export const SpaceTile = ({ space, categoryLabel, isMine, onOpen, language, t }) => {
  const banner = space.pages?.welcome?.banner || "";
  const count = (n) => formatNumber(n || 0, language);
  const members = space.stats?.memberCount ?? 0;
  const campaigns = space.stats?.campaignCount ?? 0;
  const products = (space.appPricing || []).length;
  const owner = ownerName(space.owner);
  const open = () => onOpen(space);

  return (
    <div role="link" tabIndex={0} onClick={open} onKeyDown={openOnKey(open)} className={`${CARD_SURFACE} cursor-pointer`}>
      <div className="relative flex h-[96px] w-full shrink-0 items-center justify-center overflow-hidden bg-fill-quaternary text-label-tertiary">
        {banner ? <img src={banner} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <Icon name="users" size={32} />}
      </div>
      <div className="relative -mt-space-24 flex flex-1 flex-col gap-[10px] px-space-16 pb-space-16">
        <Avatar size="l" src={space.logo} name={space.name} className="border-2 border-accent-default" />
        <div className="flex min-w-0 flex-col gap-[2px]">
          <div className="flex items-center gap-space-12">
            <h3 className="line-clamp-2 min-w-0 flex-1 text-headline text-label-primary">{space.name}</h3>
            <Button
              variant="tinted"
              size="s"
              onClick={(event) => {
                event.stopPropagation();
                open();
              }}
            >
              {isMine ? t("home.openSpace") : t("home.join")}
            </Button>
          </div>
          {categoryLabel && <p className="truncate text-caption-1 text-label-secondary">{categoryLabel}</p>}
        </div>
        {owner && (
          // Its own link inside the card: Enter here must not open the space too.
          <div className="flex min-w-0 items-center gap-space-8" onKeyDown={(event) => event.stopPropagation()}>
            <Avatar src={getProfileAvatarUrl(space.owner)} name={owner} />
            <UsernameLink
              user={space.owner}
              username={owner}
              prefix=""
              stopPropagation
              className="min-w-0 truncate text-footnote text-label-secondary"
            />
          </div>
        )}
        <ReviewsRow average={space.stats?.averageRating} count={space.stats?.ratingCount ?? 0} language={language} t={t} />
        <p className="mt-auto text-footnote text-label-secondary">
          {[
            t(members === 1 ? "home.membersOne" : "home.membersOther", { count: count(members) }),
            t(campaigns === 1 ? "home.openCampaignsOne" : "home.openCampaignsOther", { count: count(campaigns) }),
            t(products === 1 ? "home.productsOne" : "home.productsOther", { count: count(products) }),
          ].join(" · ")}
        </p>
      </div>
    </div>
  );
};
